import { API_BASE_URL } from "src/config/apiBase";
import { useState, useMemo } from "react";
import { X, Copy } from "lucide-react";
import DOMPurify from "dompurify";

/**
 * OrderDetailsModal
 * Props:
 *  - order: object (single order record from API)
 */
export default function OrderDetailsModal({ order }) {
    const [open, setOpen] = useState(false);
    const url = API_BASE_URL
    // safe parser: handles already-parsed objects or JSON strings
    const safeParse = (value) => {
        if (value == null) return null;
        try {
            if (typeof value === "string") return JSON.parse(value);
            return value;
        } catch (e) {
            // sometimes backend stores single-quoted or broken JSON; return raw
            return value;
        }
    };

    const copyToClipboard = (text) => {
        if (!text && text !== 0) return;
        navigator.clipboard.writeText(String(text));
        // lightweight non-blocking feedback
        try {
            // if your app uses toast, replace alert with toast
            alert("Copied: " + text);
        } catch (e) {
            /* ignore */
        }
    };

    // build derived data only when order changes
    const {
        orderDetailsArray,
        parsedCart,
        shipping,
        billing,
    } = useMemo(() => {
        const parsedCart = safeParse(order?.cart_products) || [];
        const shipping = safeParse(order?.shipping_address) || null;
        const billing = safeParse(order?.billing_address) || null;

        // helper for nicely formatted address string
        const fmtAddress = (addr) => {
            if (!addr) return "—";
            const parts = [];
            if (addr.new_address) parts.push(addr.new_address);
            if (addr.area) parts.push(addr.area);
            if (addr.city) parts.push(addr.city);
            if (addr.pincode) parts.push(addr.pincode);
            if (addr.name) parts.push(`(${addr.name})`);
            return parts.join(", ");
        };

        const orderId = order?.receipt_id || order?.order_id || order?.id || "—";
        const paymentStatus = order?.status || order?.payment_status || order?.orderStatus || "—";
        const orderStatus = order?.orderStatus || order?.status || "—";

        const total = (order?.amountafterdiscount ?? order?.amountbeforediscount ?? order?.total) || "0";
        const discount = order?.discountprice ?? "0";
        const deliveryCharge = order?.deliveryCharge ?? order?.delivery_charge ?? "0";

        const created = order?.createdAt ? new Date(order.createdAt).toLocaleString() : "—";
        const deliveryDate = order?.delivery_date || order?.out_deliverydate || "—";
        const paymentMode = order?.payment_mode || order?.paymentMethod || "—";
        const couponApplied = order?.couponapplied ? "Yes" : "No";

        // Build array for details table (label, value)
        const od = [
            ["Order ID", orderId],
            ["Reference ID", order?.order_id || "—"],
            ["Payment Status", paymentStatus],
            ["Order Status", orderStatus],
            ["Payment Mode", paymentMode],
            ["Total Amount", `₹${Number(total).toLocaleString()}`],
            ["Delivery Charge", `₹${Number(deliveryCharge).toLocaleString()}`],
            ["Discount", `₹${Number(discount).toLocaleString()}`],
            ["GST Amount", order?.gstamount ? `₹${order.gstamount}` : "—"],
            ["Delivery Date", deliveryDate],
            ["Created At", created],
            ["Coupon Applied", couponApplied],
            ["Billing Address", fmtAddress(billing)],
            ["Shipping Address", fmtAddress(shipping)],
            ["Customer", order?.user_name || order?.mobile || order?.email || "—"],
             ["Note", order?.note || "—"],
            ["Receipt/Ref", order?.receipt_id || "—"],
        ];

        return {
            orderDetailsArray: od,
            parsedCart,
            shipping,
            billing,
        };
    }, [order]);

    // status badge color
    const getStatusColor = (status) => {
        if (!status) return "bg-gray-100 text-gray-800";
        switch (String(status).toLowerCase()) {
            case "pending":
                return "bg-yellow-100 text-yellow-800";
            case "processing":
                return "bg-blue-100 text-blue-800";
            case "packed":
                return "bg-indigo-100 text-indigo-800";
            case "shipped":
                return "bg-purple-100 text-purple-800";
            case "outfordelivery":
            case "out for delivery":
            case "outfordelivery":
                return "bg-orange-100 text-orange-800";
            case "delivered":
                return "bg-green-100 text-green-800";
            case "cancelled":
            case "canceled":
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

 
    return (
        <>
            {/* Trigger */}
            <p
                onClick={() => setOpen(true)}
                className="text-blue-600 hover:text-blue-800 font-medium text-sm underline underline-offset-2 cursor-pointer mt-2 transition-all"
            >
                View Order Details
            </p>

            {/* Modal */}
            {open && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-start pt-16 px-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-5 relative animate-fadeIn max-h-[85vh] overflow-y-auto">
                        {/* Close */}
                        <button
                            onClick={() => setOpen(false)}
                            className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
                        >
                            <X size={20} />
                        </button>

                        {/* Title + copy button */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                                Order Details
                                <span
                                    className={`px-2 py-0.5 text-xs font-semibold rounded ${getStatusColor(
                                        order?.orderStatus || order?.status || order?.payment_status
                                    )}`}
                                >
                                    {order?.orderStatus || order?.status || order?.payment_status}
                                </span>
                            </h2>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => copyToClipboard(order?.receipt_id || order?.order_id)}
                                    className="flex items-center gap-2 px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-sm"
                                >
                                    <Copy size={14} /> Copy Order ID
                                </button>
                                {/* <button
                  onClick={() => {
                    // copy full order JSON (for admin debugging)
                    copyToClipboard(JSON.stringify(order));
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Copy JSON
                </button> */}
                            </div>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3">Ordered Products</h3>

                            {(!parsedCart || parsedCart.length === 0) ? (
                                <div className="text-sm text-gray-500">No products found in this order.</div>
                            ) : (
                                <div className="space-y-3">
                                    {parsedCart.map((item, idx) => {
                                        // normalized fields (backend inconsistency)
                                        const img = item.product_image || item.image || item.productImage || item.product_gallery?.[0]?.url;
                                        const name = item.product_name || item.name || "Product";
                                        const sku = item.product_sku || item.sku || "—";
                                        const qty = item.qty || item.quantity || item.count || 1;
                                        const price = item.product_saleprice ?? item.product_price ?? item.sale_price ?? "0";
                                        const variation = (() => {
                                            const v = item.variation_attributes;

                                            if (!v) return null;

                                            // Case 1: If it's JSON string → parse
                                            if (typeof v === "string") {
                                                try {
                                                    return JSON.parse(v);
                                                } catch {
                                                    return null;
                                                }
                                            }

                                            // Case 2: Already object → return it
                                            if (typeof v === "object") {
                                                return v;
                                            }

                                            return null;
                                        })();


                                        return (
                                            <div key={idx} className="flex items-start gap-3 border p-3 rounded-lg">
                                                <img
                                                    src={`${url}${img}` || "https://via.placeholder.com/120"}
                                                    alt={name}
                                                    className="w-20 h-20 rounded-md object-cover border"
                                                />

                                                <div className="flex-1">
                                                    <div className="flex justify-between items-start gap-2">
                                                        <div>
                                                            <p className="font-semibold text-gray-900">{name}</p>
                                                            <p className="text-xs text-gray-600">SKU: {sku}</p>
                                                            {variation && Object.keys(variation).length > 0 && (
                                                                <p className="text-xs text-gray-600">
                                                                    {Object.values(variation)
                                                                        .map(v => `${v.slug ? v.slug.toUpperCase() : "Attribute"}: ${v.name}`)
                                                                        .join(" • ")}
                                                                </p>
                                                            )}

                                                        </div>

                                                        <div className="text-right">
                                                            <p className="text-sm font-bold text-gray-900">₹{Number(price).toLocaleString()}</p>
                                                            <p className="text-xs text-gray-700">Qty: {qty}</p>
                                                        </div>
                                                    </div>

                                                    {/* Optional product description / short desc */}
                                                    {item.short_description && (
                                                        <p
                                                            className="text-xs text-gray-500 mt-2 line-clamp-3"
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(item.short_description, {
                                                                    USE_PROFILES: { html: true },
                                                                }),
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        {/* Details table */}
                        <div className="divide-y divide-gray-200 text-sm">
                            {orderDetailsArray.map(([label, value], i) => (
                                <div key={i} className="flex justify-between py-2 items-start gap-3">
                                    <span className="font-medium text-gray-600 w-[45%]">{label}:</span>

                                    <div className="w-[55%] flex justify-end">
                                        {(label.toLowerCase() === "payment status" ||
                                            label.toLowerCase() === "order status") ? (
                                            <span
                                                className={`px-2 py-1 rounded-md text-xs font-semibold ${getStatusColor(
                                                    value
                                                )}`}
                                            >
                                                {value}
                                            </span>
                                        ) : (
                                            <span className="text-gray-900 text-right break-words">{value}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Ordered Products */}


                        {/* Footer actions */}
                        <div className="mt-4 flex justify-end gap-2">
                            {/* <button
                                onClick={() => {
                                    // simple example: open Google Maps if we have lat/lng in shipping
                                    const s = safeParse(order?.shipping_address);
                                    if (s?.latitude && s?.longitude) {
                                        window.open(`https://www.google.com/maps?q=${s.latitude},${s.longitude}`, "_blank");
                                    } else {
                                        copyToClipboard(order?.shipping_address || "No location");
                                    }
                                }}
                                className="px-3 py-1 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
                            >
                                {order?.shipping_address ? "Copy Shipping" : "No Shipping"}
                            </button> */}

                            <button
                                onClick={() => setOpen(false)}
                                className="px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-sm"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
